import '../../App.css';
import { useEffect, useState } from 'react';
import { db, applicantsCol, applicationsCol } from '../../firebase';
import {
  doc, getDoc, serverTimestamp, collection, addDoc
} from 'firebase/firestore';
import { useUser } from '../../UserContext';
import { ApplicationStatusBoard } from './status';

import { useNavigate } from 'react-router-dom';

export const Preview = () => {
  const { user, loading: userLoading } = useUser();
  const userID = user?.uid || "";
  const [values, setValues] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (!userID) return;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const docRef = doc(db, applicantsCol, userID);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setValues(docSnap.data());
        } else {
          setError('No data found for this user.');
        }
      } catch (err) {
        setError('Error fetching data.');
      }
      setLoading(false);
    };
    fetchData();
  }, [userID]);

  const handleSubmitApplication = async () => {
    const applicationData = {
      status: 'submitted',
      applicantID: userID,
      university: values.university,
      level: values.level0,
      firstChoice: values.firstChoice,
      secondChoice: values.secondChoice,
      thirdChoice: values.thirdChoice,
      firstChoiceDuration: values.firstChoiceDuration,
      secondChoiceDuration: values.secondChoiceDuration,
      thirdChoiceDuration: values.thirdChoiceDuration,
      submittedAt: serverTimestamp(),
      applicantFirstName: values.fName,
      applicantMiddleName: values.mName,
      applicantLastName: values.lName,
      applicantDateOfBirth: values.dob,
      applicantEmail: values.email,
      applicantPhoneNumber: values.phoneNo,
      applicantGender: values.gender,
      applicantNationality: values.nationality,
      applicantPassportNumber: values.passportNumber,
      applicantAddress: values.personalAddress,
      accountEmail: values.applicantAccountEmail,
      accountID: values.applicantID,
      kinfName: values.kinfName,
      kinMname: values.kinMname,
      kinlName: values.kinlName,
      kinEmail: values.kinEmail,
      kinPhoneNo: values.kinPhoneNo,
      kinRelation: values.relation,
      sponsorfullName: values.sponserfName,
      sponsorEmail: values.sponserEmail,
      sponsorPhoneNo: values.sponserPhoneNo,
      sponsorAddress: values.sponserAddress,
      level: values.level ,
      levelFrom: values.levelFrom,
      formSixIndexNumber: values.formSixIndexNumber || "",
      yearCompletedFormSixOrAdvanced: values.yearCompletedFormSixOrAdvanced || "",
      nameOfInstituteCompletedDiploma: values.nameOfInstituteCompletedDiploma || "",
      diplomaStartedYear: values.diplomaStartedYear || "",
      diplomaCompletedYear: values.diplomaCompletedYear || "",
      nameOfInstituteCompletedDegree: values.nameOfInstituteCompletedDegree || "",
      degreeStartedYear: values.degreeStartedYear || "",
      degreeCompletedYear: values.degreeCompletedYear || "",
      scholarshipRequest:values.scholarship || ""
    }
    setIsSubmitting(true);
    try {
      const colRef = collection(db, applicationsCol);
      await addDoc(colRef, applicationData);
      alert('Application submitted successfully');
      setIsSubmitted(true);
          
    } catch (error) {
      alert('Error submitting application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }

  }


  if (loading) return (
    <div className="preview-loading">
      <div className="loader"></div>
      <p>Loading your application data...</p>
    </div>
  );
  
  if (error) return (
    <div className="preview-error">
      <div className="alert alert-danger">{error}</div>
    </div>
  );
  
  if (!values) return null;
  
  if(isSubmitted) return <ApplicationStatusBoard />

  return (
    <div className="preview-container">
      {/* Warning Note */}
      <div className="preview-warning">
        <div className="warning-icon">⚠️</div>
        <div className="warning-content">
          <h4>Important Notice</h4>
          <p>
            If you have already submitted this application, please do not submit it again. 
            Check your application status on the status page. Submitting multiple applications 
            may lead to account suspension.
          </p>
        </div>
      </div>

      <div className="preview-header">
        <h2 className="preview-title">Application Preview</h2>
        <p className="preview-subtitle">Please review your information before submitting</p>
      </div>

      {/* Personal Information Section */}
      <div className="preview-section">
        <div className="section-header">
          <h3>Personal Information</h3>
          <div className="section-icon">👤</div>
        </div>
        <div className="preview-grid">
          <div className="preview-field">
            <label className="field-label">First Name</label>
            <div className="field-value">{values.fName || 'N/A'}</div>
          </div>
          <div className="preview-field">
            <label className="field-label">Middle Name</label>
            <div className="field-value">{values.mName || 'N/A'}</div>
          </div>
          <div className="preview-field">
            <label className="field-label">Last Name</label>
            <div className="field-value">{values.lName || 'N/A'}</div>
          </div>
          <div className="preview-field">
            <label className="field-label">Email Address</label>
            <div className="field-value">{values.email || 'N/A'}</div>
          </div>
          <div className="preview-field">
            <label className="field-label">Gender</label>
            <div className="field-value">{values.gender || 'N/A'}</div>
          </div>
          <div className="preview-field">
            <label className="field-label">Phone Number</label>
            <div className="field-value">{values.phoneNo || 'N/A'}</div>
          </div>
          <div className="preview-field">
            <label className="field-label">Date of Birth</label>
            <div className="field-value">{values.dob || 'N/A'}</div>
          </div>
          <div className="preview-field">
            <label className="field-label">Nationality</label>
            <div className="field-value">{values.nationality || 'N/A'}</div>
          </div>
          <div className="preview-field">
            <label className="field-label">Passport Number</label>
            <div className="field-value">{values.passportNumber || 'N/A'}</div>
          </div>
          <div className="preview-field">
            <label className="field-label">Address</label>
            <div className="field-value">{values.personalAddress || 'N/A'}</div>
          </div>
        </div>
      </div>

      {/* Course Selection Section */}
      <div className="preview-section">
        <div className="section-header">
          <h3>Course Selection</h3>
          <div className="section-icon">🎓</div>
        </div>
        <div className="preview-grid">
          <div className="preview-field full-width">
            <label className="field-label">University</label>
            <div className="field-value university-name">{values.university || 'N/A'}</div>
          </div>
          <div className="preview-field">
            <label className="field-label">Level</label>
            <div className="field-value level-badge">
              {values.level0 === "degree" ? "Bachelor Degree" : "Masters Degree"}
            </div>
          </div>
          <div className="preview-field">
            <label className="field-label">Scholarship Request</label>
            <div className="field-value level-badge">
              {values.scholarship}
            </div>
          </div>
        </div>
        
        <div className="course-choices">
          <h4>Course Choices</h4>
          <div className="course-choice-list">
            <div className="course-choice">
              <div className="choice-number">1st</div>
              <div className="choice-content">
                <div className="course-name">{values.firstChoice || 'Not selected'}</div>
                {values.firstChoiceDuration && (
                  <div className="course-duration">Duration: {values.firstChoiceDuration}</div>
                )}
              </div>
            </div>
            <div className="course-choice">
              <div className="choice-number">2nd</div>
              <div className="choice-content">
                <div className="course-name">{values.secondChoice || 'Not selected'}</div>
                {values.secondChoiceDuration && (
                  <div className="course-duration">Duration: {values.secondChoiceDuration}</div>
                )}
              </div>
            </div>
            <div className="course-choice">
              <div className="choice-number">3rd</div>
              <div className="choice-content">
                <div className="course-name">{values.thirdChoice || 'Not selected'}</div>
                {values.thirdChoiceDuration && (
                  <div className="course-duration">Duration: {values.thirdChoiceDuration}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="preview-actions">
        {isSubmitting ? (
          <div className="submitting-state">
            <div className="loader"></div>
            <p>Submitting your application...</p>
          </div>
        ) : (
          <button 
            className="preview-submit-btn" 
            onClick={handleSubmitApplication}
            disabled={!values.university || !values.firstChoice}
          >
            Submit Application
          </button>
        )}
      </div>

      <style jsx>{`
        .preview-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }

        .preview-loading, .preview-error {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          text-align: center;
        }

        .preview-warning {
          display: flex;
          align-items: flex-start;
          background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
          border: 1px solid #ffc107;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 30px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .warning-icon {
          font-size: 24px;
          margin-right: 15px;
          margin-top: 2px;
        }

        .warning-content h4 {
          margin: 0 0 8px 0;
          color: #856404;
          font-size: 18px;
        }

        .warning-content p {
          margin: 0;
          color: #856404;
          line-height: 1.5;
        }

        .preview-header {
          text-align: center;
          margin-bottom: 40px;
        }

        .preview-title {
          font-size: 2.5rem;
          color: #2c3e50;
          margin-bottom: 10px;
          font-weight: 700;
        }

        .preview-subtitle {
          font-size: 1.1rem;
          color: #7f8c8d;
          margin: 0;
        }

        .preview-section {
          background: white;
          border-radius: 16px;
          padding: 30px;
          margin-bottom: 30px;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
          border: 1px solid #e9ecef;
        }

        .section-header {
          display: flex;
          align-items: center;
          margin-bottom: 25px;
          padding-bottom: 15px;
          border-bottom: 2px solid #f8f9fa;
        }

        .section-header h3 {
          margin: 0;
          font-size: 1.5rem;
          color: #2c3e50;
          font-weight: 600;
        }

        .section-icon {
          font-size: 24px;
          margin-left: 15px;
        }

        .preview-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 20px;
        }

        .preview-field {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 20px;
          border: 1px solid #e9ecef;
          transition: all 0.3s ease;
        }

        .preview-field:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .preview-field.full-width {
          grid-column: 1 / -1;
        }

        .field-label {
          display: block;
          font-size: 0.9rem;
          font-weight: 600;
          color: #6c757d;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 8px;
        }

        .field-value {
          font-size: 1.1rem;
          color: #2c3e50;
          font-weight: 500;
          word-break: break-word;
        }

        .university-name {
          font-size: 1.2rem;
          font-weight: 600;
          color: #3498db;
        }

        .level-badge {
          display: inline-block;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 0.9rem;
          font-weight: 600;
        }

        .course-choices {
          margin-top: 30px;
        }

        .course-choices h4 {
          margin-bottom: 20px;
          color: #2c3e50;
          font-size: 1.3rem;
        }

        .course-choice-list {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .course-choice {
          display: flex;
          align-items: center;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border-radius: 12px;
          padding: 20px;
          border: 1px solid #dee2e6;
          transition: all 0.3s ease;
        }

        .course-choice:hover {
          transform: translateX(5px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .choice-number {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 0.9rem;
          margin-right: 20px;
          flex-shrink: 0;
        }

        .choice-content {
          flex: 1;
        }

        .course-name {
          font-size: 1.1rem;
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 5px;
        }

        .course-duration {
          font-size: 0.9rem;
          color: #27ae60;
          font-weight: 500;
        }

        .preview-actions {
          text-align: center;
          margin-top: 40px;
        }

        .preview-submit-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 16px 40px;
          font-size: 1.1rem;
          font-weight: 600;
          border-radius: 50px;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
        }

        .preview-submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
        }

        .preview-submit-btn:disabled {
          background: #6c757d;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .submitting-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 15px;
        }

        .submitting-state p {
          color: #6c757d;
          font-size: 1rem;
        }

        .loader {
          border: 3px solid #f3f3f3;
          border-top: 3px solid #667eea;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .preview-container {
            padding: 15px;
          }

          .preview-title {
            font-size: 2rem;
          }

          .preview-section {
            padding: 20px;
          }

          .preview-grid {
            grid-template-columns: 1fr;
            gap: 15px;
          }

          .preview-warning {
            flex-direction: column;
            text-align: center;
          }

          .warning-icon {
            margin-right: 0;
            margin-bottom: 10px;
          }

          .course-choice {
            flex-direction: column;
            text-align: center;
          }

          .choice-number {
            margin-right: 0;
            margin-bottom: 15px;
          }

          .preview-submit-btn {
            width: 100%;
            max-width: 300px;
          }
        }

        @media (max-width: 480px) {
          .preview-title {
            font-size: 1.8rem;
          }

          .preview-subtitle {
            font-size: 1rem;
          }

          .section-header h3 {
            font-size: 1.3rem;
          }

          .field-value {
            font-size: 1rem;
          }

          .course-name {
            font-size: 1rem;
          }
        }
      `}</style>
    </div>
  );
};



